import { Box, Checkbox, Flex } from '@chakra-ui/react';
import { useForm, Form, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';

import { Condition } from 'Component/others';
import { TextBold, TextRegular, TextSemiBold } from 'UiKit/typography';
import InputComponent from 'UiKit/input';
import Tooltip from 'UiKit/tooltip';
import InputSelectComponent from 'UiKit/input-select';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonSolid from 'UiKit/button/buttonSolid';
import useGetRoleAccess from 'Domain/role/useGetRoleAccess';
import usePostRoleAccess from 'Domain/role/usePostRoleAccess';
import usePatchRoleAccess from 'Domain/role/usePatchRoleAccess';
import useGetOneRoleAccess from 'Domain/role/useGetOneRoleAccess';
import usePostRoleAvailability from 'Domain/role/usePostRoleAvailability';
import { useDebounce } from 'Helper/debounce';
import { AlertOutlineIcon } from 'Asset/icon';

import CheckboxAccess from 'View/role/components/checkbox-access';
import checkFormUpsertValidation from 'View/role/helper/checkFormUpsertValidation';
import checkInlineValidation from 'View/role/helper/checkInlineValidation';
import useCloseModalRestriction from 'View/role/hooks/useCloseModalRestriction';
import { useRoleState } from 'View/role/state';

import { defaultValuesForm, buildOptionsAksesWebsite } from './const';
import styles from './styles.module.css';

const FormUpsertRoleView = () => {
  const { t } = useTranslation('role');
  const params = useParams();
  const { id } = params;

  const isEditForm = !isEmpty(id);

  const { updateState } = useRoleState();

  const onCloseRestriction = useCloseModalRestriction();
  const form = useForm({ defaultValues: defaultValuesForm });
  const { control, formState, register, watch, setValue, handleSubmit, setError, clearErrors, reset } = form;
  const formField = watch();
  const { name, isHost, isOperator, accessType } = formField;

  const { errors } = formState;

  const { data: selectedRole } = useGetOneRoleAccess(id);

  const { mutate: postRoleAccess } = usePostRoleAccess(setError, clearErrors);
  const { mutate: patchRoleAccess } = usePatchRoleAccess(setError, clearErrors);
  const { mutate: checkRoleAvailablity } = usePostRoleAvailability(setError, clearErrors);

  const { data: roleAccess } = useGetRoleAccess();

  const isValidForm = checkFormUpsertValidation(formField, errors);

  const roleValidationHandler = useDebounce(checkRoleAvailablity, 300);

  const onSubmit = () => {
    if (isEditForm) patchRoleAccess(formField);
    else postRoleAccess(formField);
  };

  const onChangeInlineField = (event) => {
    const value = event.target.value;

    clearErrors('name');

    const isInlineValidation = checkInlineValidation(value, selectedRole);

    if (isInlineValidation) roleValidationHandler({ name: value });

    setValue('name', value);
  };

  const optionsAksesWebsite = buildOptionsAksesWebsite(t);

  useEffect(() => {
    if (!isEmpty(selectedRole)) reset(selectedRole);
  }, [selectedRole, reset]);

  useEffect(() => {
    if (!isEmpty(roleAccess?.hostMenu)) updateState({ hostMenu: roleAccess?.hostMenu });
  }, [roleAccess]); //eslint-disable-line

  return (
    <Box className={styles['form-upsert__container']}>
      <Box className={styles['form-upsert__header']}>
        <TextBold className={styles['form-upsert__title']}>{t(isEditForm ? 'formTitleEdit' : 'formTitleCreate')}</TextBold>
        <Condition statement={isEditForm}>
          <Box className={styles['form-upsert__header-action--edit']}>
            <ButtonOutline className={styles['form-user__button--cancel-top']} onClick={onCloseRestriction} type="button">
              <TextSemiBold>{t('buttonCancel')}</TextSemiBold>
            </ButtonOutline>

            <ButtonSolid className={styles['form-user__button--update-top']} type="button" disabled={!isValidForm} onClick={handleSubmit(onSubmit)}>
              <TextSemiBold className={styles['form-role__button--update__text']}>{t('buttonSave')}</TextSemiBold>
            </ButtonSolid>
          </Box>
        </Condition>
      </Box>

      <Box className={styles['form-upsert__body']}>
        <Form control={control} className={styles['form-role__container']} onSubmit={handleSubmit(onSubmit)} data-testid="role-form__upsert">
          <Flex className={styles['form-field__container']}>
            <InputComponent
              className={styles['form-role__input']}
              label={t('fieldRoleName')}
              value={name}
              isRequired={true}
              placeholder={t('fieldRoleNamePlaceholder')}
              error={errors.name}
              register={{ ...register('name', { required: t('fieldRoleNameRequired'), onChange: onChangeInlineField }) }}
            />

            <Flex gap={'32px'}>
              <Box className={styles['form-role__tipe-role-label']}>
                <TextBold className={styles['form-role__tipe-role-label-text']}>{t('labelRoleType')}</TextBold>
                <Tooltip label={t('tooltipRoleType')}>
                  <AlertOutlineIcon color="#215AA8" size="16px" />
                </Tooltip>
              </Box>

              <Box className={styles['form-role__tipe-role-checkboxes']}>
                <Checkbox size="md" isChecked={isHost} onChange={(e) => setValue('isHost', e.target.checked)}>
                  <TextRegular className={styles['form-role__tipe-role-checkbox-label']}>{`${t('roleTypeListedAsPrefix')} ${t('roleTypeHost')}?`}</TextRegular>
                </Checkbox>
                <Checkbox size="md" isChecked={isOperator} onChange={(e) => setValue('isOperator', e.target.checked)}>
                  <TextRegular>{`${t('roleTypeListedAsPrefix')} ${t('roleTypeOperator')}?`}</TextRegular>
                </Checkbox>
              </Box>
            </Flex>

            <Controller
              control={control}
              name={'accessType'}
              rules={{ required: true }}
              render={({ field }) => (
                <InputSelectComponent
                  label={t('fieldWebsiteAccess')}
                  isRequired={true}
                  placeholder={t('fieldWebsiteAccessPlaceholder')}
                  className={styles['form-role__select']}
                  options={optionsAksesWebsite}
                  field={field}
                  error={errors.accessType}
                  isMulti={false}
                  value={accessType}
                />
              )}
            />

            <Condition statement={accessType?.value === 'Desktop Website'}>
              <CheckboxAccess menuList={roleAccess?.operatorMenu} form={form} />
            </Condition>
          </Flex>

          <Condition statement={!isEditForm}>
            <Box className={styles['form-role__button-group']}>
              <ButtonOutline className={styles['form-role__button--cancel']} onClick={onCloseRestriction} type="button">
                <TextSemiBold className={styles['form-role__button--cancel__text']}>{t('buttonCancel')}</TextSemiBold>
              </ButtonOutline>

              <ButtonSolid className={styles['form-role__button--save']} type="submit" disabled={!isValidForm}>
                <TextSemiBold className={styles['form-role__button--save__text']}>{t('buttonSave')}</TextSemiBold>
              </ButtonSolid>
            </Box>
          </Condition>
        </Form>
      </Box>
    </Box>
  );
};

export default FormUpsertRoleView;
